import { ActivityType } from '@showbie-socrative/socrative-utils';
import { QueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { unescape } from 'underscore';

import { ActivityResponse } from '~teacher/hook';
import { queryKey } from '~teacher/hook/activity/queryKey';
import { ActivitySettingsCamelCase } from '~teacher/models/activities';

import { findStudentIdByUuid } from '../../utils';
import { StudentResponsePubnubData } from '../types';

// snake case keys from messages are mapped to camelCase local versions.

export const updateStudentResponse = produce(
  (
    activityData: ActivityResponse,
    {
      user_uuid,
      hidden,
      id,
      is_correct,
      question,
      selection_answers,
      team,
      text_answers,
    }: StudentResponsePubnubData
  ) => {
    const formattedResponse = mapStudentResponse({
      hidden,
      id,
      textAnswers: text_answers.map((textAnswer) => ({
        answerText: unescape(textAnswer.answer_text),
      })),
      isCorrect: is_correct,
      selectionAnswers: selection_answers.map((selectionAnswer) => ({
        answerId: selectionAnswer.answer_id,
      })),
      userUuid: user_uuid,
      question,
      team,
    });

    const studentId = findStudentIdByUuid(
      activityData,
      formattedResponse.userUuid
    );

    if (studentId) {
      activityData.students[studentId].isPresent = true;
    }

    // SOC-5685: an open-navigation answer edit is persisted server-side as a
    // delete + re-insert, so the updated response arrives over PubNub with a
    // NEW id. Responses are keyed by id here, so a naive insert leaves the
    // previous answer lingering under its old id and the grid keeps showing
    // the stale value until a manual refetch. Drop any prior response for the
    // same student + question before writing the new one.
    //
    // Exception: QQ Short Answer with unlimited responses. Each student submit
    // is intentionally a new row — all prior answers must be kept so the
    // teacher sees the full response history live. Derived locally from the
    // cached activity rather than a server-tagged flag, so no cross-repo
    // coupling. Scoped to QuickQuestionShortAnswer (NOT Vote, which must keep
    // replacing so its tally doesn't inflate). settings is cast to the camelCase
    // shape because localAxios camelizes response keys at runtime.
    const settings = activityData.activity
      ?.settings as unknown as ActivitySettingsCamelCase;
    const isAppend =
      activityData.activity?.activityType ===
        ActivityType.QuickQuestionShortAnswer &&
      settings?.allowRepeatResponses === true;

    if (!isAppend) {
      for (const key of Object.keys(activityData.responses)) {
        const existing = activityData.responses[key];
        if (
          existing.userUuid === formattedResponse.userUuid &&
          existing.questionId === formattedResponse.questionId &&
          existing.id !== formattedResponse.id
        ) {
          delete activityData.responses[key];
        }
      }
    }

    activityData.responses[formattedResponse.id] = formattedResponse;
  }
);

const mapStudentResponse = ({
  hidden,
  id,
  textAnswers,
  isCorrect,
  question,
  selectionAnswers,
  team,
  userUuid,
}: {
  hidden: boolean;
  id: number;
  textAnswers: Array<{ answerText: string }>;
  isCorrect: boolean;
  question: number;
  selectionAnswers: Array<{ answerId: number }>;
  team: number;
  userUuid: string;
}) => ({
  hidden: hidden,
  id: id,
  textResponse: textAnswers.map((textAnswer) => textAnswer.answerText)[0],
  isCorrect: isCorrect,
  questionId: question,
  selectedAnswerIds: selectionAnswers.map(
    (selectionAnswer) => selectionAnswer.answerId
  ),
  team,
  userUuid: userUuid,
});

export const studentResponseCacheUpdater = ({
  queryClient,
  activityInstanceId,
  rawResponse,
}: {
  queryClient: QueryClient;
  activityInstanceId: number;
  rawResponse: StudentResponsePubnubData;
}) => {
  queryClient.setQueryData(
    queryKey.byId(activityInstanceId),
    (activityData: ActivityResponse) =>
      updateStudentResponse(activityData, rawResponse)
  );
};
